import cn from 'classnames';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import LazyHydrate from 'react-lazy-hydration';

import { useIsMobile } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';
import { Tab } from '@sravni/ugc/lib/specialOffers/types';

import { SravniReviewsModelsFiltersFilterItem } from '../../../../__generated__/reviews/reviewsApiTypes';
import { listInfiniteScroll } from '../../../../helpers/listInfiniteScroll';
import { ReviewType } from '../../../../helpers/reviews';
import { IReviewsListState } from '../../../../store/reducers/reviews';
import { NoResult } from '../../../NoResult';
import { ReviewSkeleton } from '../../../Skeletons/review';
import { SpecialOffersAndActionsBlock } from '../../../SpecialOffersBlock';
import { useFetchLikesAndSetForInitialReviews } from '../../hooks/useFetchLikesAndSetForInitialReviews';
import { ListCard } from '../Card';

import styles from './styles.module.scss';

const VALID_SPECIAL_OFFERS_INDEX = 2;

enum HydrationLimit {
  Start = 5,
  End = 20,
}

export interface IListContentProps {
  reviews: IReviewsListState;
  type: PAGE_TYPE;
  tags: SravniReviewsModelsFiltersFilterItem[];
  handleDropFilters?: () => void;
  reviewTypes: ReviewType;
  specialOffersTabs: Tab[];
  handlePageScroll: () => void;
  isReviewPage?: boolean;
}

export const ReviewsListContent: React.FC<IListContentProps> = ({
  reviews,
  reviewTypes,
  type,
  tags,
  handleDropFilters,
  specialOffersTabs,
  handlePageScroll,
  isReviewPage,
}) => {
  const isMobile = useIsMobile();
  const { isFetchingReviewLikes } = useFetchLikesAndSetForInitialReviews(reviews);

  const triggerRef = useRef();
  const triggerRemoveHandlerRef = useRef<() => void>();
  const { items, loading, isLoadingMore } = reviews;

  useEffect(() => {
    // если у нас сменился handleLoadMore переподписываемся
    if (handlePageScroll && triggerRef.current) {
      if (triggerRemoveHandlerRef.current) {
        triggerRemoveHandlerRef.current();
      }
      const { remove } = listInfiniteScroll(handlePageScroll, triggerRef.current);

      triggerRemoveHandlerRef.current = remove;

      return () => {
        if (triggerRemoveHandlerRef.current) {
          triggerRemoveHandlerRef.current();
        }
      };
    }
    // eslint-disable-next-line consistent-return
    return;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [handlePageScroll]);

  const specialOffersBlock = (
    <div className={cn({ 'h-ml-20 h-mr-20': isMobile })}>
      <SpecialOffersAndActionsBlock tabs={specialOffersTabs} />
    </div>
  );

  const reviewsList = useMemo(() => {
    return (items || []).map((item, index) => {
      const isLazyHydrate = index > HydrationLimit.Start && index <= HydrationLimit.End;
      const Wrapper = isLazyHydrate ? LazyHydrate : React.Fragment;
      const props = isLazyHydrate ? { whenVisible: true } : {};

      const reviewCard = (
        <>
          <ListCard review={item} type={type} tags={tags} reviewTypes={reviewTypes} isMobile={isMobile} />
          {!isLazyHydrate && index === VALID_SPECIAL_OFFERS_INDEX && specialOffersBlock}
        </>
      );

      return (
        <div key={item.id}>
          <Wrapper {...props}>{reviewCard}</Wrapper>
        </div>
      );
    });
  }, [items, type, tags, reviewTypes, isReviewPage]);

  const isSkeletonVisible = isFetchingReviewLikes || (loading && !isLoadingMore);
  const isNoResultVisible = !isSkeletonVisible && isEmpty(items) && !isReviewPage;

  return (
    <div className={styles.wrapper}>
      {isSkeletonVisible && <ReviewSkeleton />}
      {isNoResultVisible && <NoResult handleClick={handleDropFilters} />}
      <div className={styles.trigger} ref={triggerRef}></div>
      {!isSkeletonVisible && !isNoResultVisible && reviewsList}
    </div>
  );
};
